import React from 'react';
import { Helmet } from 'react-helmet';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';

const HospitalRCM = () => {
  const pageSchema = {
    "@context": "https://schema.org",
    "@type": "Service",
    "name": "Hospital Revenue Cycle Management Services",
    "description": "Comprehensive hospital revenue cycle management solutions. Reduce claim denials, accelerate reimbursements, and improve cash flow with expert RCM services for hospitals and health systems.",
    "provider": {
      "@type": "MedicalBusiness",
      "name": "MDeRCM",
      "url": "https://mdercm.com",
      "address": {
        "@type": "PostalAddress",
        "streetAddress": "Five Greentree Centre, 525 Route 73 North, Suite 104",
        "addressLocality": "Marlton",
        "addressRegion": "NJ",
        "postalCode": "08053",
        "addressCountry": "US"
      }
    },
    "areaServed": "United States"
  };

  const challenges = [
    { icon: '❌', title: 'High Denial Rates', description: '15-20% of hospital claims denied on first submission' },
    { icon: '⏱️', title: 'Long Payment Cycles', description: 'Average 45-60 days from service to payment' },
    { icon: '💸', title: 'Revenue Leakage', description: 'Up to 5% revenue lost due to coding errors' },
    { icon: '📊', title: 'Complex Regulations', description: 'Constantly changing payer rules and compliance requirements' },
    { icon: '👥', title: 'Staffing Challenges', description: 'High turnover and training costs for billing teams' },
    { icon: '🔍', title: 'Lack of Visibility', description: 'No real-time insights into revenue cycle performance' }
  ];

  const solutions = [
    {
      icon: '🤖',
      title: 'AI-Powered Automation',
      description: 'Automate claims processing, coding, and submission to reduce manual errors and accelerate reimbursements.'
    },
    {
      icon: '✅',
      title: 'Pre-Authorization Management',
      description: 'Streamlined prior auth process with real-time insurance verification and automated approval tracking.'
    },
    {
      icon: '💰',
      title: 'Denial Prevention',
      description: 'AI-driven claim scrubbing and validation to catch errors before submission, reducing denial rates by 40%.'
    },
    {
      icon: '📈',
      title: 'Real-Time Analytics',
      description: 'Comprehensive dashboard with KPIs, cash flow tracking, and performance metrics for informed decisions.'
    },
    {
      icon: '🔄',
      title: 'Denial Management',
      description: 'Expert team handles appeals and resubmissions with 95%+ success rate on denied claims.'
    },
    {
      icon: '🏥',
      title: 'Hospital-Specific Expertise',
      description: 'Specialized knowledge of inpatient coding, DRG validation, and complex hospital billing scenarios.'
    }
  ];

  const benefits = [
    { metric: '35-45%', label: 'Reduction in Claim Denials' },
    { metric: '30-50%', label: 'Lower Operational Costs' },
    { metric: '15-20 Days', label: 'Faster Payment Cycle' },
    { metric: '98%+', label: 'First-Pass Claim Rate' }
  ];

  return (
    <>
      <Helmet>
        <title>Hospital Revenue Cycle Management | MDeRCM</title>
        <meta 
          name="description" 
          content="Comprehensive hospital RCM services. Reduce denials 35-45%, improve cash flow and maximize reimbursements with AI-powered billing."
        />
        <meta name="keywords" content="hospital revenue cycle management, hospital RCM, revenue cycle management for hospitals, hospital billing services, inpatient RCM" />
        <link rel="canonical" href="https://mdercm.com/hospital-revenue-cycle-management" />
        
        <meta property="og:title" content="Hospital Revenue Cycle Management Services | MDeRCM" />
        <meta property="og:description" content="Expert hospital RCM - Reduce denials 35-45%, improve cash flow. Trusted by 100+ hospitals." />
        <meta property="og:url" content="https://mdercm.com/hospital-revenue-cycle-management" />
        
        <script type="application/ld+json">
          {JSON.stringify(pageSchema)}
        </script>
      </Helmet>

      <main style={{ fontFamily: "'Poppins', sans-serif" }}>
        {/* Hero Section */}
        <section style={{
          background: 'linear-gradient(135deg, #0077b6 0%, #003545 100%)',
          padding: '120px 5% 80px',
          color: '#ffffff'
        }}>
          <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
            >
              <h1 style={{
                fontSize: 'clamp(2.2rem, 5vw, 3.5rem)',
                fontWeight: '800',
                marginBottom: '25px',
                lineHeight: '1.2'
              }}>
                Hospital Revenue Cycle Management
              </h1>
              <h2 style={{
                fontSize: 'clamp(1.3rem, 3vw, 2rem)',
                fontWeight: '600',
                marginBottom: '20px',
                color: '#F26522'
              }}>
                Optimize Your Hospital's Financial Performance
              </h2>
              <p style={{
                fontSize: '1.2rem',
                lineHeight: '1.8',
                maxWidth: '800px',
                marginBottom: '35px'
              }}>
                Transform your <strong>hospital revenue cycle management</strong> with AI-powered solutions that reduce denials, accelerate cash flow, and improve operational efficiency across your health system.
              </p>
              <div style={{ display: 'flex', gap: '20px', flexWrap: 'wrap' }}>
                <Link to="/free-trial" style={{
                  display: 'inline-block',
                  padding: '15px 35px',
                  backgroundColor: '#F26522',
                  color: '#ffffff',
                  borderRadius: '30px',
                  textDecoration: 'none',
                  fontWeight: '700',
                  fontSize: '1.1rem',
                  transition: 'all 0.3s ease'
                }}
                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#d3541c'}
                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#F26522'}
                >
                  Schedule Demo
                </Link>
                <Link to="/why" style={{
                  display: 'inline-block',
                  padding: '15px 35px',
                  backgroundColor: 'transparent',
                  color: '#ffffff',
                  border: '2px solid #ffffff',
                  borderRadius: '30px',
                  textDecoration: 'none',
                  fontWeight: '700',
                  fontSize: '1.1rem',
                  transition: 'all 0.3s ease'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = '#ffffff';
                  e.currentTarget.style.color = '#003545';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = 'transparent';
                  e.currentTarget.style.color = '#ffffff';
                }}
                >
                  Learn More
                </Link>
              </div>
            </motion.div>
          </div>
        </section>

        {/* Stats */}
        <section style={{ padding: '60px 5%', backgroundColor: '#f8f9fa' }}>
          <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
              gap: '30px'
            }}>
              {benefits.map((stat, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, scale: 0.9 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  transition={{ delay: index * 0.1 }}
                  viewport={{ once: true }}
                  style={{
                    padding: '35px',
                    backgroundColor: '#ffffff',
                    borderRadius: '12px',
                    textAlign: 'center',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.08)'
                  }}
                >
                  <div style={{ fontSize: '2.8rem', fontWeight: '800', color: '#0077b6', marginBottom: '10px' }}>
                    {stat.metric}
                  </div>
                  <div style={{ fontSize: '1rem', color: '#666', fontWeight: '500' }}>
                    {stat.label}
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Challenges */}
        <section style={{ padding: '80px 5%', backgroundColor: '#ffffff' }}>
          <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
            <h2 style={{
              fontSize: 'clamp(2rem, 4vw, 2.8rem)',
              fontWeight: '700',
              textAlign: 'center',
              marginBottom: '20px',
              color: '#1A1A1A'
            }}>
              Common Hospital RCM Challenges
            </h2>
            <p style={{
              fontSize: '1.1rem',
              textAlign: 'center',
              maxWidth: '800px',
              margin: '0 auto 50px',
              color: '#666'
            }}>
              Hospitals face unique revenue cycle challenges that impact cash flow and operational efficiency.
            </p>

            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
              gap: '30px'
            }}>
              {challenges.map((challenge, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, x: -30 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  transition={{ delay: index * 0.1 }}
                  viewport={{ once: true }}
                  style={{
                    padding: '30px',
                    backgroundColor: '#fff5f0',
                    borderRadius: '12px',
                    borderLeft: '4px solid #F26522'
                  }}
                >
                  <div style={{ fontSize: '3rem', marginBottom: '15px' }}>{challenge.icon}</div>
                  <h3 style={{ fontSize: '1.3rem', fontWeight: '700', marginBottom: '10px', color: '#d32f2f' }}>
                    {challenge.title}
                  </h3>
                  <p style={{ fontSize: '1rem', color: '#666', lineHeight: '1.6' }}>
                    {challenge.description}
                  </p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Solutions */}
        <section style={{ padding: '80px 5%', backgroundColor: '#f8f9fa' }}>
          <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
            <h2 style={{
              fontSize: 'clamp(2rem, 4vw, 2.8rem)',
              fontWeight: '700',
              textAlign: 'center',
              marginBottom: '20px',
              color: '#1A1A1A'
            }}>
              Our Hospital RCM Solutions
            </h2>
            <p style={{
              fontSize: '1.1rem',
              textAlign: 'center',
              maxWidth: '800px',
              margin: '0 auto 50px',
              color: '#666'
            }}>
              Comprehensive <strong>hospital revenue cycle management</strong> services designed to maximize your financial performance.
            </p>

            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))',
              gap: '30px'
            }}>
              {solutions.map((solution, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                  viewport={{ once: true }}
                  style={{
                    padding: '35px',
                    backgroundColor: '#ffffff',
                    borderRadius: '12px',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
                    transition: 'all 0.3s ease'
                  }}
                  whileHover={{
                    boxShadow: '0 10px 30px rgba(0,0,0,0.12)',
                    transform: 'translateY(-5px)'
                  }}
                >
                  <div style={{ fontSize: '3.5rem', marginBottom: '15px' }}>{solution.icon}</div>
                  <h3 style={{ fontSize: '1.3rem', fontWeight: '700', marginBottom: '12px', color: '#0077b6' }}>
                    {solution.title}
                  </h3>
                  <p style={{ fontSize: '1rem', color: '#555', lineHeight: '1.7' }}>
                    {solution.description}
                  </p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Why Choose Us */}
        <section style={{ padding: '80px 5%', backgroundColor: '#ffffff' }}>
          <div style={{ maxWidth: '1000px', margin: '0 auto' }}>
            <h2 style={{
              fontSize: 'clamp(2rem, 4vw, 2.8rem)',
              fontWeight: '700',
              textAlign: 'center',
              marginBottom: '50px',
              color: '#1A1A1A'
            }}>
              Why Choose MDeRCM for Hospital RCM?
            </h2>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '30px' }}>
              {[
                { title: 'Hospital-Specific Expertise', desc: 'Deep knowledge of inpatient coding, DRG validation, and complex hospital billing scenarios.' },
                { title: 'Proven Track Record', desc: '100+ hospitals trust us with their revenue cycle. Average 35% reduction in denials within 6 months.' },
                { title: 'Advanced Technology', desc: 'AI-powered platform integrated with major EMR systems for seamless data flow and automation.' },
                { title: 'Dedicated Support', desc: 'Assigned account manager and billing specialists who understand your hospital\'s unique needs.' },
                { title: 'Compliance & Security', desc: 'Full HIPAA compliance, ISO certifications, and SOC 2 Type II audit-ready infrastructure.' }
              ].map((item, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, x: -30 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  transition={{ delay: index * 0.1 }}
                  viewport={{ once: true }}
                  style={{
                    padding: '30px',
                    backgroundColor: '#f8f9fa',
                    borderRadius: '12px',
                    borderLeft: '5px solid #0077b6'
                  }}
                >
                  <h3 style={{ fontSize: '1.4rem', fontWeight: '700', marginBottom: '12px', color: '#0077b6' }}>
                    ✅ {item.title}
                  </h3>
                  <p style={{ fontSize: '1.05rem', color: '#666', lineHeight: '1.7' }}>
                    {item.desc}
                  </p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section style={{
          padding: '80px 5%',
          background: 'linear-gradient(135deg, #0077b6 0%, #003545 100%)',
          color: '#ffffff',
          textAlign: 'center'
        }}>
          <div style={{ maxWidth: '900px', margin: '0 auto' }}>
            <h2 style={{
              fontSize: 'clamp(2rem, 4vw, 2.8rem)',
              fontWeight: '700',
              marginBottom: '20px'
            }}>
              Transform Your Hospital's Revenue Cycle
            </h2>
            <p style={{
              fontSize: '1.2rem',
              marginBottom: '35px',
              lineHeight: '1.8'
            }}>
              Partner with MDeRCM for comprehensive <strong>hospital revenue cycle management</strong> that delivers measurable results. Schedule your free consultation today!
            </p>
            <div style={{ display: 'flex', gap: '20px', justifyContent: 'center', flexWrap: 'wrap' }}>
              <Link to="/free-trial" style={{
                display: 'inline-block',
                padding: '18px 40px',
                backgroundColor: '#F26522',
                color: '#ffffff',
                borderRadius: '30px',
                textDecoration: 'none',
                fontWeight: '700',
                fontSize: '1.1rem',
                transition: 'all 0.3s ease'
              }}
              onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#d3541c'}
              onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#F26522'}
              >
                Schedule Free Demo
              </Link>
              <a href="tel:+15103566069" style={{
                display: 'inline-block',
                padding: '18px 40px',
                backgroundColor: 'transparent',
                color: '#ffffff',
                border: '2px solid #ffffff',
                borderRadius: '30px',
                textDecoration: 'none',
                fontWeight: '700',
                fontSize: '1.1rem',
                transition: 'all 0.3s ease'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = '#ffffff';
                e.currentTarget.style.color = '#003545';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'transparent';
                e.currentTarget.style.color = '#ffffff';
              }}
              >
                📞 Call Us Now
              </a>
            </div>
          </div>
        </section>

        {/* Related */}
        <section style={{ padding: '60px 5%', backgroundColor: '#f8f9fa' }}>
          <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
            <h3 style={{
              fontSize: '1.8rem',
              fontWeight: '700',
              textAlign: 'center',
              marginBottom: '40px',
              color: '#1A1A1A'
            }}>
              Related Services
            </h3>
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
              gap: '25px'
            }}>
              {[
                { title: 'Medical Billing Outsourcing', link: '/medical-billing-outsourcing', icon: '💼' },
                { title: 'RCM Consulting', link: '/revenue-cycle-consulting', icon: '📊' },
                { title: 'Denial Management', link: '/healthcare/ai/denial-management', icon: '🔄' },
                { title: 'AI Healthcare Solutions', link: '/healthcare/ai', icon: '🤖' }
              ].map((item, index) => (
                <Link key={index} to={item.link} style={{
                  padding: '30px',
                  backgroundColor: '#ffffff',
                  borderRadius: '12px',
                  textAlign: 'center',
                  textDecoration: 'none',
                  color: '#333',
                  border: '2px solid #e9ecef',
                  transition: 'all 0.3s ease',
                  display: 'block'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.borderColor = '#0077b6';
                  e.currentTarget.style.transform = 'translateY(-5px)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.borderColor = '#e9ecef';
                  e.currentTarget.style.transform = 'translateY(0)';
                }}
                >
                  <div style={{ fontSize: '3rem', marginBottom: '15px' }}>{item.icon}</div>
                  <h4 style={{ fontSize: '1.2rem', fontWeight: '600', color: '#0077b6' }}>
                    {item.title}
                  </h4>
                </Link>
              ))}
            </div>
          </div>
        </section>
      </main>
    </>
  );
};

export default HospitalRCM;